import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../components/Layout";
import RoleMiddleware from "../components/RoleMiddleware";
import { hasAnyRole, isPureTechnician } from "../lib/access-control";
import Dashboard from "../pages/protected/Dashboard";
import DoctorHome from "../pages/protected/doctor/DoctorHome";
import MyAppointments from "../pages/protected/doctor/MyAppointments";
import MyPatients from "../pages/protected/doctor/MyPatients";
import MyBooking from "../pages/protected/doctor/MyBooking";
import Patients from "../pages/protected/Patients";
import Doctors from "../pages/protected/Doctors";
import Appointments from "../pages/protected/Appointments";
import Stats from "../pages/protected/Stats";
import DoctorAttendance from "../pages/protected/DoctorAttendance";
import OpdBooking from "../pages/protected/OpdBooking";
import Messages from "../pages/protected/Messages";
import UserMaster from "../pages/protected/UserMaster";
import CallCenter from "../pages/protected/CallCenter";
import Billing from "../pages/protected/Billing";
import LabOrders from "../pages/protected/LabOrders";
import AddLabOrder from "../pages/protected/AddLabOrder";
import LabTestMaster from "../pages/protected/LabTestMaster";
import LabMaster from "../pages/protected/LabMaster";
import ActivityLog from "../pages/protected/ActivityLog";
import Prescriptions from "../pages/protected/Prescriptions";
import WriteRx from "../pages/protected/WriteRx";
import MedicineMaster from "../pages/protected/MedicineMaster";
import ClinicalTermMaster from "../pages/protected/ClinicalTermMaster";
import DepartmentMaster from "../pages/protected/DepartmentMaster";
import Settings from "../pages/protected/Settings";
import PlatformAdmin from "../pages/protected/PlatformAdmin";
import DiagnosticWorklist, { TechnicianHome } from "../pages/protected/DiagnosticWorklist";
import DiagnosticDepartmentMaster from "../pages/protected/DiagnosticDepartmentMaster";
import Leads from "../pages/protected/Leads";
import WhatsAppInbox from "../pages/protected/WhatsAppInbox";
import LeadInbox from "../pages/protected/LeadInbox";
import { IS_DEMO } from "../lib/env";

// A pure doctor account (no caredesk/finance/master/super-admin) has no use
// for the clinic-wide Dashboard (its summary widgets are gated off from them
// at the API layer anyway) — render their own home page directly instead.
function Home() {
  const user = useSelector((s) => s.users.user);
  // Aivexa-internal, exclusive to Platform Admin — never lands on this
  // clinic's own Dashboard, even if it also happens to hold a clinic role.
  if (user?.platform_admin) return <Navigate to="/platform-admin" replace />;
  // A technician's whole dashboard is their department worklist.
  if (isPureTechnician(user)) return <TechnicianHome />;
  const isPureDoctor =
    hasAnyRole(user, ["doctor"]) && !hasAnyRole(user, ["caredesk", "finance", "master", "super-admin"]);
  return isPureDoctor ? <DoctorHome /> : <Dashboard />;
}

export default function MainNavigation() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/my-appointments"
          element={
            <RoleMiddleware roles={["doctor"]} module="doctor_portal">
              <MyAppointments />
            </RoleMiddleware>
          }
        />
        <Route
          path="/my-patients"
          element={
            <RoleMiddleware roles={["doctor"]} module="doctor_portal">
              <MyPatients />
            </RoleMiddleware>
          }
        />
        <Route
          path="/my-booking"
          element={
            <RoleMiddleware roles={["doctor"]} module="doctor_portal">
              <MyBooking />
            </RoleMiddleware>
          }
        />
        <Route
          path="/opd"
          element={
            <RoleMiddleware roles={["caredesk"]} module="opd">
              <OpdBooking />
            </RoleMiddleware>
          }
        />
        <Route
          path="/appointments"
          element={
            <RoleMiddleware roles={["caredesk"]} module="appointments">
              <Appointments />
            </RoleMiddleware>
          }
        />
        <Route
          path="/stats"
          element={
            <RoleMiddleware roles={["super-admin", "admin"]} module="stats">
              <Stats />
            </RoleMiddleware>
          }
        />
        <Route
          path="/doctor-attendance"
          element={
            <RoleMiddleware roles={["caredesk"]} module="doctor_attendance">
              <DoctorAttendance />
            </RoleMiddleware>
          }
        />
        <Route
          path="/messages"
          element={
            <RoleMiddleware roles={["caredesk"]} module="messages">
              <Messages />
            </RoleMiddleware>
          }
        />
        <Route
          path="/patients"
          element={
            <RoleMiddleware roles={["caredesk", "master"]} module="patients">
              <Patients />
            </RoleMiddleware>
          }
        />
        <Route
          path="/prescriptions"
          element={
            <RoleMiddleware roles={["caredesk"]} module="prescriptions">
              <Prescriptions />
            </RoleMiddleware>
          }
        />
        <Route
          path="/prescriptions/:appointmentId"
          element={
            <RoleMiddleware roles={["caredesk"]} module="prescriptions">
              <WriteRx />
            </RoleMiddleware>
          }
        />
        <Route
          path="/lab-orders"
          element={
            <RoleMiddleware roles={["caredesk"]} module="lab_orders">
              <LabOrders />
            </RoleMiddleware>
          }
        />
        <Route
          path="/lab-orders/new"
          element={
            <RoleMiddleware roles={["caredesk"]} module="lab_orders">
              <AddLabOrder />
            </RoleMiddleware>
          }
        />
        <Route
          path="/diagnostics/:departmentId"
          element={
            <RoleMiddleware roles={["technician", "caredesk"]} module="diagnostics">
              <DiagnosticWorklist />
            </RoleMiddleware>
          }
        />
        <Route
          path="/lead-inbox"
          element={
            <RoleMiddleware roles={["caredesk"]} module="leads">
              <LeadInbox />
            </RoleMiddleware>
          }
        />
        {IS_DEMO && (
          <Route
            path="/whatsapp"
            element={
              <RoleMiddleware roles={["caredesk"]}>
                <WhatsAppInbox />
              </RoleMiddleware>
            }
          />
        )}
        {IS_DEMO && (
          <Route
            path="/leads"
            element={
              <RoleMiddleware roles={["caredesk"]}>
                <Leads />
              </RoleMiddleware>
            }
          />
        )}
        <Route
          path="/activity"
          element={
            <RoleMiddleware roles={["caredesk", "finance", "master"]} module="activity">
              <ActivityLog />
            </RoleMiddleware>
          }
        />
        <Route
          path="/call-center"
          element={
            <RoleMiddleware roles={["caredesk"]} requiresFeature="click_to_call_enabled">
              <CallCenter />
            </RoleMiddleware>
          }
        />

        <Route
          path="/doctors"
          element={
            <RoleMiddleware roles={["master", "admin"]} module="doctors">
              <Doctors />
            </RoleMiddleware>
          }
        />
        <Route
          path="/lab-catalog"
          element={
            <RoleMiddleware roles={["master"]} module="lab_catalog">
              <LabTestMaster />
            </RoleMiddleware>
          }
        />
        <Route
          path="/labs"
          element={
            <RoleMiddleware roles={["master"]} module="labs">
              <LabMaster />
            </RoleMiddleware>
          }
        />
        <Route
          path="/diagnostic-departments"
          element={
            <RoleMiddleware roles={["master"]} module="diagnostic_departments">
              <DiagnosticDepartmentMaster />
            </RoleMiddleware>
          }
        />
        <Route
          path="/medicines"
          element={
            <RoleMiddleware roles={["master"]} module="medicines">
              <MedicineMaster />
            </RoleMiddleware>
          }
        />
        <Route
          path="/clinical-terms"
          element={
            <RoleMiddleware roles={["master"]} module="clinical_terms">
              <ClinicalTermMaster />
            </RoleMiddleware>
          }
        />
        <Route
          path="/departments"
          element={
            <RoleMiddleware roles={["master"]} module="departments">
              <DepartmentMaster />
            </RoleMiddleware>
          }
        />
        <Route
          path="/settings"
          element={
            <RoleMiddleware roles={["master"]}>
              <Settings />
            </RoleMiddleware>
          }
        />

        <Route
          path="/billing"
          element={
            <RoleMiddleware roles={["finance"]} module="billing">
              <Billing />
            </RoleMiddleware>
          }
        />
        <Route
          path="/users"
          element={
            <RoleMiddleware roles={["super-admin"]}>
              <UserMaster />
            </RoleMiddleware>
          }
        />

        <Route
          path="/platform-admin"
          element={
            <RoleMiddleware requiresPlatformAdmin>
              <PlatformAdmin />
            </RoleMiddleware>
          }
        />

        {/* Anything unmatched (e.g. a stale/typo'd URL) — home rather than a
            silent blank page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
